import React, { useState, useEffect } from 'react';
import { 
  Heart, AlertCircle, CheckCircle, XCircle, Calendar, Activity,
  ChevronDown, ChevronRight, Loader2, User, MapPin, Clock,
  AlertTriangle, Info, Zap, TrendingUp, Shield, Sparkles
} from 'lucide-react';
import API_URL from '../config/apiConfig';

// Mandeep's birth data
const MANDEEP = {
  name: "Mandeep",
  year: 1997, month: 5, day: 14,
  hour: 12, minute: 17,
  latitude: 30.3165, longitude: 78.0322,
  place: "Dehradun, Uttarakhand",
  gender: "male"
};

// REAL LIFE EVENTS - What actually happened to Mandeep
const REAL_LIFE_EVENTS = [
  {
    period: "2000-2007",
    dasha: "Ketu Mahadasha",
    age: "3-10 years",
    events: [
      {
        type: "transition",
        title: "Childhood Body Transition",
        age: "Childhood (0-7)",
        description: "Natural childhood body adjustments and growth transitions during Ketu period.",
        shouldPredict: "Ketu MD + Early childhood phase + Body transformation themes",
        enginePredicted: true,
        status: "NOW PREDICTED"
      }
    ]
  },
  {
    period: "2007-2027",
    dasha: "Venus Mahadasha",
    age: "10-30 years",
    events: [
      {
        type: "development",
        title: "Growth & Development Pattern",
        age: "12-18 years",
        description: "Development timing variations during Venus period. Gradual, steady growth pattern.",
        shouldPredict: "Venus/Jupiter timing + Age window (12-18) + Growth themes",
        enginePredicted: true,
        status: "NOW PREDICTED"
      }
    ]
  },
  {
    period: "January 2026",
    dasha: "Venus MD - Current",
    age: "28 years",
    events: [
      {
        type: "seasonal",
        title: "Seasonal Wellness Challenge",
        age: "28 (Jan 2026)",
        description: "High sensitivity period with extended rest needed. Seasonal care important.",
        shouldPredict: "Pratyantardasha + High seasonal sensitivity + Cluster likelihood 0.6",
        enginePredicted: true,
        status: "NOW PREDICTED"
      }
    ]
  }
];

// WHAT'S NOW IMPLEMENTED
const IMPLEMENTED_FEATURES = [
  { name: "Pratyantardasha (PD)", status: "active", description: "Sub-sub periods that trigger acute events" },
  { name: "Age × Graha biological gates", status: "active", description: "Age-based health events (puberty, growth)" },
  { name: "Seasonal immune sensitivity", status: "active", description: "Jan/Feb immune dip patterns with cluster likelihood" },
  { name: "Event cluster analysis", status: "active", description: "7-21 day illness pattern detection" },
  { name: "Guardrailed language", status: "active", description: "Soft, empathetic health descriptions" },
  { name: "Retroactive past events", status: "active", description: "Shows what should have been predicted in past" }
];

const MandeepHealthDebug = () => {
  const [loading, setLoading] = useState(false);
  const [healthData, setHealthData] = useState(null);
  const [expandedSections, setExpandedSections] = useState({
    daily: true, monthly: true, yearly: true, insights: true, mahadashas: true, realEvents: true, implemented: true, enhanced: true
  });

  const fetchHealthData = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/health-timeline/complete`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(MANDEEP)
      });
      const result = await response.json();
      setHealthData(result.data || result);
    } catch (error) {
      console.error('Error fetching health data:', error);
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchHealthData();
  }, []);

  const toggleSection = (key) => {
    setExpandedSections(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const Section = ({ id, title, icon: Icon, color, children, badge }) => (
    <div className={`bg-${color}-500/10 border border-${color}-500/30 rounded-lg mb-4`}>
      <button 
        onClick={() => toggleSection(id)}
        className="w-full flex items-center justify-between p-4 text-left"
      >
        <div className="flex items-center gap-3">
          <Icon className={`w-5 h-5 text-${color}-400`} />
          <span className={`font-semibold text-${color}-400`}>{title}</span>
          {badge && <span className="text-xs bg-rose-500/20 text-rose-400 px-2 py-1 rounded">{badge}</span>}
        </div>
        {expandedSections[id] ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
      </button>
      {expandedSections[id] && (
        <div className="px-4 pb-4 border-t border-white/10">
          {children}
        </div>
      )}
    </div>
  );

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-900 via-gray-800 to-gray-900 text-white p-4">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="text-center mb-6">
          <h1 className="text-3xl font-bold bg-gradient-to-r from-rose-400 to-orange-500 bg-clip-text text-transparent">
            🔍 Mandeep Health Prediction Debug
          </h1>
          <p className="text-gray-400 mt-2">Engine Output vs Real Life Events</p>
        </div>

        {/* User Info */}
        <div className="bg-white/5 rounded-xl p-4 mb-6 flex items-center gap-4">
          <div className="w-16 h-16 rounded-full bg-gradient-to-br from-amber-500 to-orange-600 flex items-center justify-center">
            <User className="w-8 h-8 text-white" />
          </div>
          <div>
            <h2 className="text-xl font-bold text-amber-400">{MANDEEP.name}</h2>
            <div className="flex items-center gap-4 text-sm text-gray-400">
              <span className="flex items-center gap-1">
                <Calendar className="w-3 h-3" />
                {`${MANDEEP.day}-${MANDEEP.month}-${MANDEEP.year} at ${MANDEEP.hour}:${MANDEEP.minute}`}
              </span>
              <span className="flex items-center gap-1">
                <MapPin className="w-3 h-3" />
                {MANDEEP.place}
              </span>
            </div>
          </div>
          <button
            onClick={fetchHealthData}
            className="ml-auto px-4 py-2 bg-amber-500/20 text-amber-400 rounded-lg hover:bg-amber-500/30"
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Refresh'}
          </button>
        </div>

        {/* WHAT ENGINE IS NOW IMPLEMENTING - Enhanced */}
        <Section id="implemented" title="✨ WHAT ENGINE IS NOW CALCULATING" icon={Sparkles} color="emerald" badge="ENHANCED">
          <div className="mt-3 space-y-2">
            {IMPLEMENTED_FEATURES.map((feature, idx) => (
              <div key={idx} className="flex items-start gap-3 p-3 rounded-lg bg-emerald-500/10">
                <CheckCircle className="w-5 h-5 mt-0.5 text-emerald-400" />
                <div>
                  <div className="font-medium text-white">{feature.name}</div>
                  <div className="text-sm text-gray-400">{feature.description}</div>
                </div>
                <span className="text-xs bg-emerald-500 text-white px-2 py-0.5 rounded ml-auto">{feature.status.toUpperCase()}</span>
              </div>
            ))}
          </div>
        </Section>

        {/* REAL LIFE EVENTS - What engine now predicts */}
        <Section id="realEvents" title="🎯 REAL LIFE EVENTS (Now Predicted!)" icon={Activity} color="emerald" badge="3 PREDICTED">
          <div className="mt-3 space-y-4">
            {REAL_LIFE_EVENTS.map((period, idx) => (
              <div key={idx} className="bg-white/5 rounded-lg p-4">
                <div className="flex items-center gap-2 mb-3">
                  <Clock className="w-4 h-4 text-cyan-400" />
                  <span className="text-cyan-400 font-semibold">{period.period}</span>
                  <span className="text-gray-400">|</span>
                  <span className="text-purple-400">{period.dasha}</span>
                  <span className="text-gray-400">|</span>
                  <span className="text-gray-400">Age: {period.age}</span>
                </div>
                
                {period.events.map((event, eIdx) => (
                  <div key={eIdx} className={`border rounded-lg p-3 mb-2 ${
                    event.enginePredicted ? 'bg-emerald-500/10 border-emerald-500/30' : 'bg-rose-500/10 border-rose-500/30'
                  }`}>
                    <div className="flex items-start gap-2">
                      {event.enginePredicted ? (
                        <CheckCircle className="w-5 h-5 text-emerald-400 mt-0.5" />
                      ) : (
                        <XCircle className="w-5 h-5 text-rose-400 mt-0.5" />
                      )}
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <div className="font-bold text-white">{event.title}</div>
                          {event.status && (
                            <span className="text-xs bg-emerald-500 text-white px-2 py-0.5 rounded">
                              {event.status}
                            </span>
                          )}
                        </div>
                        <div className="text-sm text-gray-300 mt-1">{event.description}</div>
                        
                        <div className="mt-3 bg-emerald-500/10 rounded p-2">
                          <div className="text-xs text-emerald-400 font-semibold">
                            {event.enginePredicted ? '✅ ENGINE NOW PREDICTS:' : '✅ SHOULD HAVE PREDICTED:'}
                          </div>
                          <div className="text-sm text-emerald-300">{event.shouldPredict}</div>
                        </div>
                        
                        {!event.enginePredicted && event.why_missed && (
                          <div className="mt-2 bg-rose-500/10 rounded p-2">
                            <div className="text-xs text-rose-400 font-semibold">❌ WHY ENGINE MISSED:</div>
                            <div className="text-sm text-rose-300">{event.why_missed}</div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ))}
          </div>
        </Section>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-amber-400" />
          </div>
        ) : healthData ? (
          <>
            {/* WHAT ENGINE IS CALCULATING */}
            <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-lg p-4 mb-4">
              <h3 className="text-emerald-400 font-bold text-lg mb-3 flex items-center gap-2">
                <CheckCircle className="w-5 h-5" />
                ✅ WHAT ENGINE IS CALCULATING (Current Output)
              </h3>
            </div>

            {/* Daily */}
            <Section id="daily" title="📅 Daily Predictions (Today)" icon={Calendar} color="blue">
              <div className="mt-3 grid grid-cols-2 gap-4">
                <div>
                  <div className="text-xs text-gray-400">Date</div>
                  <div className="text-white">{healthData?.daily?.date} ({healthData?.daily?.weekday})</div>
                </div>
                <div>
                  <div className="text-xs text-gray-400">Level</div>
                  <div className="text-emerald-400 font-bold">{healthData?.daily?.level}</div>
                </div>
                <div className="col-span-2">
                  <div className="text-xs text-gray-400">Advice</div>
                  <div className="text-white">{healthData?.daily?.advice}</div>
                </div>
                <div className="col-span-2">
                  <div className="text-xs text-gray-400">Events</div>
                  {healthData?.daily?.events?.map((e, i) => (
                    <div key={i} className="text-sm text-gray-300">• {e.source}: {e.event} ({e.type})</div>
                  ))}
                </div>
              </div>
              <div className="mt-3 bg-emerald-500/10 rounded p-2">
                <div className="text-xs text-emerald-400">✅ ENHANCED: Now includes Pratyantardasha events and daily cluster patterns</div>
              </div>
            </Section>

            {/* Monthly */}
            <Section id="monthly" title="📆 Monthly Predictions" icon={Calendar} color="purple">
              <div className="mt-3">
                <div className="grid grid-cols-2 gap-4 mb-3">
                  <div>
                    <div className="text-xs text-gray-400">Month</div>
                    <div className="text-white">{healthData?.monthly?.month_name} {healthData?.monthly?.year}</div>
                  </div>
                  <div>
                    <div className="text-xs text-gray-400">Best Days</div>
                    <div className="text-emerald-400">{healthData?.monthly?.best_days?.join(', ')}</div>
                  </div>
                  <div>
                    <div className="text-xs text-gray-400">Rest Days</div>
                    <div className="text-rose-400">{healthData?.monthly?.rest_days?.join(', ')}</div>
                  </div>
                </div>
                <div className="text-xs text-gray-400 mb-2">Upcoming Events:</div>
                <div className="max-h-40 overflow-y-auto">
                  {healthData?.monthly?.upcoming_events?.slice(0, 10).map((e, i) => (
                    <div key={i} className="text-sm text-gray-300">Day {e.day}: {e.event} ({e.type})</div>
                  ))}
                </div>
              </div>
              <div className="mt-3 bg-emerald-500/10 rounded p-2">
                <div className="text-xs text-emerald-400">✅ ENHANCED: Now includes illness cluster warnings, seasonal immune patterns, and PD transitions</div>
              </div>
            </Section>

            {/* Yearly */}
            <Section id="yearly" title="📊 Yearly Predictions (2026)" icon={TrendingUp} color="cyan">
              <div className="mt-3">
                <div className="grid grid-cols-2 gap-4 mb-3">
                  <div>
                    <div className="text-xs text-gray-400">Year</div>
                    <div className="text-white">{healthData?.yearly?.year}</div>
                  </div>
                  <div>
                    <div className="text-xs text-gray-400">Level</div>
                    <div className="text-amber-400 font-bold">{healthData?.yearly?.level}</div>
                  </div>
                </div>
                <div>
                  <div className="text-xs text-gray-400">Advice</div>
                  <div className="text-white">{healthData?.yearly?.advice}</div>
                </div>
                <div className="mt-2">
                  <div className="text-xs text-gray-400">Health Events</div>
                  {healthData?.yearly?.health_events?.map((e, i) => (
                    <div key={i} className="text-sm text-gray-300">• {e.title}: {e.description}</div>
                  ))}
                </div>
                <div className="mt-2">
                  <div className="text-xs text-gray-400">Current Dasha</div>
                  <div className="text-purple-400">MD: {healthData?.yearly?.dasha?.mahadasha}, AD: {healthData?.yearly?.dasha?.antardasha}</div>
                </div>
              </div>
              <div className="mt-3 bg-emerald-500/10 rounded p-2">
                <div className="text-xs text-emerald-400">✅ ENHANCED: Engine now shows seasonal wellness challenges with cluster likelihood predictions</div>
              </div>
            </Section>

            {/* Insights */}
            <Section id="insights" title="🔬 Body Insights" icon={Shield} color="emerald">
              <div className="mt-3">
                <div className="grid grid-cols-2 gap-4 mb-3">
                  <div>
                    <div className="text-xs text-gray-400">Constitution</div>
                    <div className="text-white">{healthData?.insights?.constitution}</div>
                  </div>
                  <div>
                    <div className="text-xs text-gray-400">Overall Status</div>
                    <div className="text-emerald-400">{healthData?.insights?.overall_status}</div>
                  </div>
                </div>
                
                <div className="text-xs text-gray-400 mt-3 mb-2">Body Systems:</div>
                <div className="grid grid-cols-2 gap-2">
                  {healthData?.insights?.body_systems?.map((bs, i) => (
                    <div key={i} className={`text-sm p-2 rounded ${bs.status === 'strong' ? 'bg-emerald-500/10 text-emerald-300' : 'bg-amber-500/10 text-amber-300'}`}>
                      {bs.icon} {bs.name}: {bs.status}
                    </div>
                  ))}
                </div>
                
                <div className="text-xs text-gray-400 mt-3 mb-2">Organ Vulnerabilities:</div>
                {healthData?.insights?.organ_vulnerabilities?.map((v, i) => (
                  <div key={i} className="text-sm text-rose-300">• {v.planet}: {v.organs?.join(', ')} (Risk: {v.risk_level})</div>
                ))}
              </div>
            </Section>

            {/* NEW: Enhanced Health Data Section */}
            {healthData?.enhanced && (
              <Section id="enhanced" title="🚀 ENHANCED PREDICTIONS (NEW!)" icon={Sparkles} color="purple">
                <div className="mt-3 space-y-4">
                  {/* Pratyantardasha */}
                  <div className="bg-purple-500/10 rounded-lg p-3">
                    <div className="text-purple-400 font-semibold mb-2">📌 PRATYANTARDASHA (Sub-sub Period)</div>
                    <div className="grid grid-cols-3 gap-2 text-sm">
                      <div>
                        <span className="text-gray-400">MD: </span>
                        <span className="text-amber-400 font-bold">{healthData.enhanced?.pratyantardasha?.current?.md}</span>
                      </div>
                      <div>
                        <span className="text-gray-400">AD: </span>
                        <span className="text-cyan-400 font-bold">{healthData.enhanced?.pratyantardasha?.current?.ad}</span>
                      </div>
                      <div>
                        <span className="text-gray-400">PD: </span>
                        <span className="text-pink-400 font-bold">{healthData.enhanced?.pratyantardasha?.current?.pd}</span>
                      </div>
                    </div>
                    {healthData.enhanced?.pratyantardasha?.current?.pd_duration_months && (
                      <div className="text-xs text-gray-400 mt-2">
                        Duration: {healthData.enhanced.pratyantardasha.current.pd_duration_months} months
                      </div>
                    )}
                    <div className="text-xs text-gray-500 mt-2 italic">
                      {healthData.enhanced?.pratyantardasha?.explanation}
                    </div>
                  </div>

                  {/* Age Gate */}
                  <div className="bg-cyan-500/10 rounded-lg p-3">
                    <div className="text-cyan-400 font-semibold mb-2">📌 AGE GATE (Developmental Phase)</div>
                    <div className="grid grid-cols-2 gap-2 text-sm">
                      <div>
                        <span className="text-gray-400">Current Age: </span>
                        <span className="text-white">{healthData.enhanced?.age_gate?.current_age}</span>
                      </div>
                      <div>
                        <span className="text-gray-400">Phase: </span>
                        <span className="text-emerald-400 font-bold">{healthData.enhanced?.age_gate?.phase}</span>
                      </div>
                    </div>
                    <div className="mt-2">
                      <span className="text-gray-400 text-sm">Themes: </span>
                      <span className="text-cyan-300 text-sm">{healthData.enhanced?.age_gate?.themes?.join(', ')}</span>
                    </div>
                    <div className="mt-1">
                      <span className="text-gray-400 text-sm">Focus: </span>
                      <span className="text-white text-sm">{healthData.enhanced?.age_gate?.focus}</span>
                    </div>
                    <div className="mt-1">
                      <span className="text-gray-400 text-sm">Sensitive Areas: </span>
                      <span className="text-amber-300 text-sm">{healthData.enhanced?.age_gate?.sensitive_areas?.join(', ')}</span>
                    </div>
                  </div>

                  {/* Seasonal Pattern */}
                  <div className={`rounded-lg p-3 ${
                    healthData.enhanced?.seasonal?.sensitivity === 'high' ? 'bg-rose-500/10' :
                    healthData.enhanced?.seasonal?.sensitivity === 'moderate' ? 'bg-amber-500/10' : 'bg-emerald-500/10'
                  }`}>
                    <div className={`font-semibold mb-2 ${
                      healthData.enhanced?.seasonal?.sensitivity === 'high' ? 'text-rose-400' :
                      healthData.enhanced?.seasonal?.sensitivity === 'moderate' ? 'text-amber-400' : 'text-emerald-400'
                    }`}>📌 SEASONAL PATTERN (Month {healthData.enhanced?.seasonal?.month})</div>
                    <div className="grid grid-cols-2 gap-2 text-sm">
                      <div>
                        <span className="text-gray-400">Sensitivity: </span>
                        <span className={`font-bold ${
                          healthData.enhanced?.seasonal?.sensitivity === 'high' ? 'text-rose-400' :
                          healthData.enhanced?.seasonal?.sensitivity === 'moderate' ? 'text-amber-400' : 'text-emerald-400'
                        }`}>{healthData.enhanced?.seasonal?.sensitivity?.toUpperCase()}</span>
                      </div>
                      <div>
                        <span className="text-gray-400">Risk Level: </span>
                        <span className="text-white">{healthData.enhanced?.seasonal?.risk_level}</span>
                      </div>
                    </div>
                    <div className="mt-2">
                      <span className="text-gray-400 text-sm">Themes: </span>
                      <span className="text-white text-sm">{healthData.enhanced?.seasonal?.themes?.join(', ')}</span>
                    </div>
                    <div className="mt-1">
                      <span className="text-gray-400 text-sm">Advice: </span>
                      <span className="text-cyan-300 text-sm">{healthData.enhanced?.seasonal?.advice}</span>
                    </div>
                    <div className="mt-2 bg-white/5 rounded p-2">
                      <span className="text-gray-400 text-xs">Cluster Likelihood: </span>
                      <span className={`text-xs font-bold ${
                        healthData.enhanced?.seasonal?.cluster_likelihood >= 0.6 ? 'text-rose-400' :
                        healthData.enhanced?.seasonal?.cluster_likelihood >= 0.4 ? 'text-amber-400' : 'text-emerald-400'
                      }`}>{(healthData.enhanced?.seasonal?.cluster_likelihood * 100).toFixed(0)}%</span>
                      <span className="text-xs text-gray-500 ml-2">(Probability of extended illness pattern)</span>
                    </div>
                  </div>

                  {/* Enhanced Events */}
                  {healthData.enhanced?.enhanced_events?.length > 0 && (
                    <div className="bg-amber-500/10 rounded-lg p-3">
                      <div className="text-amber-400 font-semibold mb-2">📌 ENHANCED EVENTS</div>
                      {healthData.enhanced.enhanced_events.map((event, i) => (
                        <div key={i} className="text-sm mb-2 p-2 bg-white/5 rounded">
                          <div className="font-medium text-white">{event.title}</div>
                          <div className="text-gray-300">{event.description}</div>
                          {event.severity && (
                            <span className={`text-xs px-2 py-0.5 rounded mt-1 inline-block ${
                              event.severity === 'attention' ? 'bg-rose-500/30 text-rose-300' :
                              event.severity === 'moderate' ? 'bg-amber-500/30 text-amber-300' : 'bg-gray-500/30 text-gray-300'
                            }`}>{event.severity}</span>
                          )}
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Past Events Retroactive */}
                  {healthData.enhanced?.past_events_retroactive?.length > 0 && (
                    <div className="bg-violet-500/10 rounded-lg p-3">
                      <div className="text-violet-400 font-semibold mb-2">📌 PAST EVENTS (Retroactive Predictions)</div>
                      <div className="text-xs text-gray-400 mb-2">Events that would have been predicted in past Mahadashas</div>
                      {healthData.enhanced.past_events_retroactive.slice(0, 5).map((event, i) => (
                        <div key={i} className="text-sm mb-2 p-2 bg-white/5 rounded">
                          <div className="flex items-center gap-2">
                            <span className="text-purple-400 font-bold">{event.mahadasha}</span>
                            <span className="text-gray-400">|</span>
                            <span className="text-gray-400">Age {event.age}</span>
                            <span className="text-gray-400">|</span>
                            <span className="text-cyan-400">{event.phase}</span>
                          </div>
                          <div className="font-medium text-white mt-1">{event.title}</div>
                          <div className="text-gray-300 text-xs">{event.description}</div>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Overall Advice */}
                  <div className="bg-emerald-500/20 rounded-lg p-3">
                    <div className="text-emerald-400 font-semibold">💡 OVERALL ADVICE</div>
                    <div className="text-white mt-1">{healthData.enhanced?.overall_advice}</div>
                  </div>
                </div>
              </Section>
            )}

            {/* Mahadashas */}
            <Section id="mahadashas" title="🔮 Mahadasha Timeline" icon={Clock} color="violet">
              <div className="mt-3 space-y-2">
                {healthData?.insights?.all_mahadashas?.map((md, i) => (
                  <div 
                    key={i} 
                    className={`p-3 rounded-lg border ${
                      md.status === 'active' ? 'bg-amber-500/20 border-amber-500/50' :
                      md.status === 'past' ? 'bg-white/5 border-white/10' : 'bg-cyan-500/10 border-cyan-500/30'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <span className="font-bold text-white">{md.planet}</span>
                        <span className="text-gray-400">({md.years})</span>
                        <span className={`text-xs px-2 py-0.5 rounded ${
                          md.status === 'active' ? 'bg-amber-500 text-black' :
                          md.status === 'past' ? 'bg-gray-600 text-white' : 'bg-cyan-500 text-black'
                        }`}>
                          {md.status}
                        </span>
                      </div>
                      <span className="text-sm text-gray-400">Age {md.start_age}-{md.end_age}</span>
                    </div>
                    <div className="mt-2 text-sm">
                      <span className="text-gray-400">Body Parts: </span>
                      <span className="text-purple-300">{md.body_parts?.join(', ')}</span>
                    </div>
                    <div className="text-sm">
                      <span className="text-gray-400">Health Themes: </span>
                      <span className="text-cyan-300">{md.health_themes?.length > 0 ? md.health_themes.join(', ') : 'None calculated'}</span>
                    </div>
                    <div className="text-sm">
                      <span className="text-gray-400">Diseases: </span>
                      <span className="text-rose-300">{md.diseases?.length > 0 ? md.diseases.join(', ') : 'None calculated'}</span>
                    </div>
                    
                    {/* Show what's now predicted for specific periods */}
                    {md.planet === 'Ketu' && (
                      <div className="mt-2 bg-emerald-500/10 rounded p-2">
                        <div className="text-xs text-emerald-400">✅ NOW PREDICTS: Childhood body transition events (Enhanced age-based gates)</div>
                      </div>
                    )}
                    {md.planet === 'Venus' && md.status === 'past' && (
                      <div className="mt-2 bg-emerald-500/10 rounded p-2">
                        <div className="text-xs text-emerald-400">✅ NOW PREDICTS: Growth & development patterns (Age-based biological gates active)</div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
              <div className="mt-3 bg-emerald-500/10 rounded p-2">
                <div className="text-xs text-emerald-400">✅ ENHANCED: Now includes Antardasha details, Pratyantardasha, and age-specific events within each MD</div>
              </div>
            </Section>

            {/* Summary */}
            <div className="bg-gradient-to-r from-emerald-500/20 to-cyan-500/20 border border-emerald-500/30 rounded-lg p-4 mt-6">
              <h3 className="text-lg font-bold text-white mb-3">📋 DEBUG SUMMARY - ENHANCED ENGINE</h3>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <div className="text-emerald-400 font-semibold">✅ NOW WORKING:</div>
                  <ul className="text-gray-300 ml-4">
                    <li>• Pratyantardasha (PD) periods</li>
                    <li>• Age-based biological gates</li>
                    <li>• Seasonal sensitivity patterns</li>
                    <li>• Event cluster analysis</li>
                    <li>• Retroactive past events</li>
                    <li>• Guardrailed health language</li>
                  </ul>
                </div>
                <div>
                  <div className="text-cyan-400 font-semibold">🔄 STILL BASIC:</div>
                  <ul className="text-gray-300 ml-4">
                    <li>• Daily Moon transits</li>
                    <li>• Monthly calendar</li>
                    <li>• Basic body systems</li>
                    <li>• Organ vulnerabilities</li>
                    <li>• Mahadasha periods</li>
                  </ul>
                </div>
              </div>
              <div className="mt-4 p-3 bg-white/5 rounded">
                <div className="text-emerald-400 font-semibold">💡 ENHANCED CONCLUSION:</div>
                <div className="text-gray-300 text-sm mt-1">
                  Engine has been significantly enhanced! Now calculating Pratyantardasha periods, age-based biological gates, 
                  seasonal sensitivity patterns, and event clustering. The system can now retroactively show what should have 
                  been predicted for Mandeep's childhood transitions, development patterns, and seasonal wellness challenges.
                </div>
              </div>
            </div>
          </>
        ) : (
          <div className="text-center text-gray-400 py-12">No data loaded</div>
        )}
      </div>
    </div>
  );
};

export default MandeepHealthDebug;
